import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, UserPlus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/form-field';
import { apiAcceptInvitation } from '@/features/users/api';
import { extractErrorMessage } from '@/lib/api-client';

const schema = z.object({
  name: z.string().min(3, 'Informe seu nome').max(120),
  password: z.string().min(10, 'Mínimo 10 caracteres'),
  phone: z.string().max(20).optional(),
});

type FormData = z.infer<typeof schema>;

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ email: string } | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', password: '', phone: '' },
  });

  if (!token) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>Link de convite inválido. Peça um novo ao administrador.</AlertDescription>
        </Alert>
        <Link to="/login" className="text-sm text-primary hover:underline">Ir para o login</Link>
      </div>
    );
  }

  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-success/10 text-success">
          <CheckCircle className="size-7" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold">Conta criada!</h2>
          <p className="text-sm text-muted-foreground">
            Sua conta com o email <strong>{success.email}</strong> está pronta.
          </p>
        </div>
        <Button asChild className="w-full" size="lg">
          <Link to="/login">Fazer login</Link>
        </Button>
      </div>
    );
  }

  const onSubmit = async (values: FormData) => {
    setError(null);
    try {
      const result = await apiAcceptInvitation({ token, ...values });
      setSuccess({ email: result.email });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h2 className="text-3xl font-semibold tracking-tight">Aceitar convite</h2>
        <p className="text-sm text-muted-foreground">
          Complete seus dados para criar sua conta.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <FormField control={form.control} name="name" label="Nome completo" placeholder="Seu nome" autoFocus />
        <FormField control={form.control} name="password" label="Senha" type="password" placeholder="Mínimo 10 caracteres" hint="Use pelo menos 10 caracteres." />
        <FormField control={form.control} name="phone" label="Telefone (opcional)" placeholder="+55 11 99999-9999" />

        <Button type="submit" size="lg" className="w-full" loading={form.formState.isSubmitting}>
          <UserPlus className="size-4" /> Criar conta
        </Button>
      </form>
    </div>
  );
}
